import { ChevronDown, ChevronRight } from 'lucide-react';
import { memo, useMemo, useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { formatJson } from '@/_shared/lib/format';
import { formatSql } from '@/_shared/lib/sql';
import { SyntaxHighlighter } from '@/_shared/lib/syntax';
import { chatArtifactKey, chatArtifactSourceId } from '@/chat/lib/chat-artifacts';
import { dashboardArtifactCardsFromItems, normalizeDashboardActivityItems } from '@/chat/lib/dashboard-activity';
import type { DashboardArtifactOpenHint } from '@/chat/lib/dashboard-draft';
import type { ResolvedDashboardArtifact } from '@/chat/lib/resolved-dashboard-artifact';

import { DashboardArtifactCard } from './artifacts/DashboardArtifactCard';
import { isContextRetrievalTool } from './context-tool-result';
import { ContextToolResult } from './ContextToolResult';
import { StickyArtifactSlot } from './StickyArtifactSlot';
import type { ActivityItem } from './tool-call-meta';
import { ToolIcon } from './ToolIcons';

/** Pretty-print SQL detail so an agent's single-line query reads cleanly; pass other languages through. */
function detailContent(detail: { language: string; content: string }): string {
  return detail.language === 'sql' ? formatSql(detail.content) : detail.content;
}

/** Syntax-highlighted detail block. The `useSyntaxStyle` subscription lives here — not on the row —
 *  so only *open* rows subscribe to theme changes (a collapsed activity list costs no listeners). */
function DetailCode({
  detail,
  customStyle,
}: {
  detail: { language: string; content: string };
  customStyle: React.CSSProperties;
}): React.ReactElement {
  const syntaxStyle = useSyntaxStyle();
  return (
    <SyntaxHighlighter language={detail.language} style={syntaxStyle} customStyle={customStyle}>
      {detailContent(detail)}
    </SyntaxHighlighter>
  );
}

/** The dashboard edit tools, labelled `title ?? slug` like the backend summary (`getToolSummary`). */
const DASHBOARD_EDIT_TOOLS = new Set([
  'mcp__dashboard-draft__preview_dashboard',
  'mcp__dashboard-draft__update_dashboard',
  'mcp__dashboardEdit__update_dashboard',
]);

/** Extract a short distinguishing label from tool args when summary is empty/generic. */
function extractArgLabel(name: string, args: unknown): string {
  if (!args || typeof args !== 'object') return '';
  const a = args as Record<string, unknown>;
  if (DASHBOARD_EDIT_TOOLS.has(name)) {
    // A panel-patch call (ACT2-1078) usually sends no title; the slug still names the dashboard.
    for (const key of ['title', 'slug']) {
      const v = a[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
    return '';
  }
  // Try common identifier fields
  for (const key of [
    'source_name',
    'name',
    'source_id',
    'destination_id',
    'id',
    'path',
    'file_path',
    'query',
    'pattern',
  ]) {
    const v = a[key];
    if (typeof v === 'string' && v.length > 0) {
      const display = v.length > 60 ? v.slice(0, 60) + '\u2026' : v;
      return display;
    }
  }
  // describe_table passes tables: [{ schema?, table }] — without this the preferred tool call
  // shows no table at all when the backend summary is empty.
  const tables = a['tables'];
  if (Array.isArray(tables)) {
    const names = tables.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      const item = entry as Record<string, unknown>;
      const table = typeof item['table'] === 'string' ? item['table'].trim() : '';
      if (!table) return [];
      const schema = typeof item['schema'] === 'string' ? item['schema'].trim() : '';
      return [schema ? `${schema}.${table}` : table];
    });
    if (names.length > 0) {
      const joined = names.join(', ');
      return joined.length > 60 ? joined.slice(0, 60) + '\u2026' : joined;
    }
  }
  return '';
}

// ---------------------------------------------------------------------------
// Sub-grouping: collapse consecutive items with the same action
// ---------------------------------------------------------------------------

interface SubGroup {
  action: string;
  items: ActivityItem[];
}

function buildSubGroups(items: ActivityItem[]): SubGroup[] {
  const groups: SubGroup[] = [];
  let current: SubGroup | null = null;

  for (const item of items) {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- optional chain narrows to `never` here (TS 5.9 bug)
    if (current && current.action === item.toolUse.action) {
      current.items.push(item);
    } else {
      if (current) groups.push(current);
      current = { action: item.toolUse.action, items: [item] };
    }
  }
  if (current) groups.push(current);
  return groups;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ActivityGroupProps {
  items: ActivityItem[];
  isActive?: boolean;
  /** Last assistant message before this tool group — shown as a compact context label. */
  context?: string;
  /** Slugs already saved in the transcript before this activity group. */
  savedSlugsBefore?: Set<string>;
  onDashboardArtifactOpen?: (slug: string, hint?: DashboardArtifactOpenHint) => void;
  resolveDashboardArtifact?: (slug: string) => ResolvedDashboardArtifact | null;
  /** Key of the artifact pinned to the top of the transcript — that card yields to the pinned copy. */
  stickyArtifactKey?: string | null;
  /** Transcript index of this activity group — half of each card's pin-observer position id. */
  startIndex?: number;
  /** Identity → position id of the card the pin should observe. */
  stickySources?: ReadonlyMap<string, string>;
}

// ---------------------------------------------------------------------------
// ActivityItemRow — single tool-use row (expandable)
// ---------------------------------------------------------------------------

function ActivityItemRow({
  item,
  isLast,
  isActive,
}: {
  item: ActivityItem;
  isLast: boolean;
  isActive: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const showPulse = isLast && isActive && !item.toolResult;
  const detailLabel =
    item.toolUse.summary !== '' && item.toolUse.summary != null
      ? item.toolUse.summary
      : extractArgLabel(item.toolUse.name, item.toolUse.args);
  const isContextRetrieval = isContextRetrievalTool(item.toolUse.name);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <li>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ToolIcon category={item.toolUse.category} action={item.toolUse.action} />
            <span className="flex-1 truncate">
              {item.toolUse.friendlyName}
              {detailLabel ? <span className="ml-1.5 text-muted-foreground/60">{detailLabel}</span> : null}
            </span>
            {showPulse && (
              <span
                className="animate-thinking-dot inline-block h-2 w-2 rounded-full bg-brand"
                data-testid="active-indicator"
              />
            )}
            {open ? (
              <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight size={12} className="shrink-0 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border px-3 py-2 text-xs">
            {isContextRetrieval ? (
              item.toolResult ? (
                <ContextToolResult toolName={item.toolUse.name} content={item.toolResult.content} />
              ) : (
                <p className="text-muted-foreground">Waiting for context…</p>
              )
            ) : item.toolUse.detail ? (
              <div className="overflow-hidden rounded-lg border border-border">
                <DetailCode
                  detail={item.toolUse.detail}
                  customStyle={{ margin: 0, padding: '0.75rem', fontSize: '0.8125rem' }}
                />
              </div>
            ) : (
              <>
                {item.toolUse.args != null && (
                  <pre className="mb-2 overflow-x-auto rounded bg-background p-2 text-muted-foreground">
                    {formatJson(item.toolUse.args)}
                  </pre>
                )}
              </>
            )}
            {!isContextRetrieval && item.toolResult && (
              <>
                <div className="my-1 border-t border-border" />
                <pre className="max-h-48 overflow-auto rounded bg-background p-2 text-muted-foreground">
                  {item.toolResult.content}
                </pre>
              </>
            )}
          </div>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// SubGroupRow — collapsed row for 2+ consecutive items with same action
// ---------------------------------------------------------------------------

function SubGroupRow({
  subGroup,
  isLast,
  isActive,
}: {
  subGroup: SubGroup;
  isLast: boolean;
  isActive: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const count = subGroup.items.length;
  const firstItem = subGroup.items[0]!;

  // Build label from groupedTitle template or fallback
  const groupedTitle = firstItem.toolUse.groupedTitle;
  const label = groupedTitle
    ? groupedTitle.replace('{count}', String(count))
    : `${firstItem.toolUse.friendlyName} x ${count}`;

  // Show pulse if this is the last sub-group and any item lacks a result
  const showPulse = isLast && isActive && subGroup.items.some((item) => !item.toolResult);

  const effectiveOpen = open;

  return (
    <Collapsible open={effectiveOpen} onOpenChange={setOpen} asChild>
      <li>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
            data-testid="sub-group-trigger"
          >
            <ToolIcon category={firstItem.toolUse.category} action={firstItem.toolUse.action} />
            <span className="flex-1 truncate">{label}</span>
            {showPulse && (
              <span
                className="animate-thinking-dot inline-block h-2 w-2 rounded-full bg-brand"
                data-testid="active-indicator"
              />
            )}
            {effectiveOpen ? (
              <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight size={12} className="shrink-0 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="list-none border-t border-border">
            {subGroup.items.map((item) => {
              const isContextRetrieval = isContextRetrievalTool(item.toolUse.name);
              return (
                <li key={item.toolUse.id} className="px-3 py-1">
                  <div className="text-xs text-muted-foreground/60">
                    {item.toolUse.summary !== '' && item.toolUse.summary != null
                      ? item.toolUse.summary
                      : extractArgLabel(item.toolUse.name, item.toolUse.args) || item.toolUse.friendlyName}
                  </div>
                  {isContextRetrieval ? (
                    <div className="mt-1">
                      {item.toolResult ? (
                        <ContextToolResult toolName={item.toolUse.name} content={item.toolResult.content} />
                      ) : (
                        <p className="text-muted-foreground">Waiting for context…</p>
                      )}
                    </div>
                  ) : item.toolUse.detail ? (
                    <div className="mt-1 overflow-hidden rounded border border-border">
                      <DetailCode
                        detail={item.toolUse.detail}
                        customStyle={{ margin: 0, padding: '0.5rem', fontSize: '0.75rem' }}
                      />
                    </div>
                  ) : null}
                  {!isContextRetrieval && item.toolResult && (
                    <pre className="mt-1 max-h-24 overflow-auto rounded bg-background p-2 text-[0.6875rem] text-muted-foreground/50">
                      {item.toolResult.content}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// ActivityGroup — top-level grouping component
// ---------------------------------------------------------------------------

const NO_SAVED_SLUGS = new Set<string>();

function ActivityGroupImpl({
  items,
  isActive = false,
  context,
  savedSlugsBefore,
  onDashboardArtifactOpen,
  resolveDashboardArtifact,
  stickyArtifactKey = null,
  startIndex = 0,
  stickySources,
}: ActivityGroupProps): React.ReactElement | null {
  // `dashboardArtifactCardsFromItems` JSON.parses every dashboard spec in the group, so keep it off the
  // path of re-renders this group's own inputs did not cause (a preview panel opening, a pin handover).
  const { subGroups, artifactCards } = useMemo(() => {
    const normalized = normalizeDashboardActivityItems(items, savedSlugsBefore ?? NO_SAVED_SLUGS);
    return {
      subGroups: buildSubGroups(normalized),
      artifactCards: dashboardArtifactCardsFromItems(normalized, resolveDashboardArtifact),
    };
  }, [items, savedSlugsBefore, resolveDashboardArtifact]);

  // Normalization can drop every row (e.g. a redundant save_dashboard on an already-saved slug),
  // leaving nothing to show. Render nothing instead of an empty bordered box — which otherwise
  // reads as a stray divider under the message.
  if (subGroups.length === 0 && artifactCards.length === 0) return null;

  return (
    <div className="mx-6 my-1.5" data-testid="activity-group">
      {context && (
        <p className="mb-1 text-sm text-muted-foreground" data-testid="activity-context">
          {context}
        </p>
      )}
      {subGroups.length > 0 && (
        <div className={`rounded-lg border border-border bg-muted ${isActive ? 'activity-active' : ''}`}>
          <ul className="list-none divide-y divide-border">
            {subGroups.map((sg, i) => {
              const isLastSg = i === subGroups.length - 1;
              if (sg.items.length === 1) {
                return (
                  <ActivityItemRow
                    key={sg.items[0]!.toolUse.id}
                    item={sg.items[0]!}
                    isLast={isLastSg}
                    isActive={isActive}
                  />
                );
              }
              return <SubGroupRow key={sg.items[0]!.toolUse.id} subGroup={sg} isLast={isLastSg} isActive={isActive} />;
            })}
          </ul>
        </div>
      )}
      {artifactCards.length > 0 ? (
        <div className="mt-2 flex flex-col gap-2">
          {artifactCards.map((card, cardIndex) => (
            <StickyArtifactSlot
              key={card.slug}
              artifactKey={chatArtifactKey('dashboard', card.slug)}
              // Per-CARD, not per-group: two dashboards in one group would otherwise be indistinguishable
              // to the pin, which then cannot tell which of them it is observing.
              sourceId={chatArtifactSourceId(startIndex, cardIndex)}
              stickySources={stickySources}
              stickyArtifactKey={stickyArtifactKey}
            >
              <DashboardArtifactCard
                title={card.title}
                slug={card.slug}
                panelCount={card.panelCount}
                saved={card.saved}
                isDraft={card.isDraft}
                previewAvailable={!!resolveDashboardArtifact?.(card.slug) || card.saved}
                onOpen={() =>
                  onDashboardArtifactOpen?.(card.slug, {
                    title: card.title,
                    saved: card.saved,
                    dashboardId: card.dashboardId,
                  })
                }
              />
            </StickyArtifactSlot>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Memoized: a transcript can hold dozens of groups, and each render re-parses its dashboard specs. Nothing
 * here depends on state outside its props, so an unrelated re-render (preview panel, pin handover) can skip
 * every group whose own inputs are unchanged.
 */
export const ActivityGroup = memo(ActivityGroupImpl);
